import { useBridgeApi } from '@/api/BridgeApi';

import type { ProcessStatus } from '../../../bridge/types/process.ts';

interface GeneratingImagesArtifact {
	id: string;
	process_id?: string | null;
	content: {
		image_urls?: string[];
		last_generation_error?: unknown;
	};
}

export function useIsGeneratingImages(
	artifact: GeneratingImagesArtifact,
	processStatus?: ProcessStatus | null,
): boolean {
	const bridgeApi = useBridgeApi();
	const outstandingRequests = bridgeApi.user_state?.outstanding_requests ?? {};
	const isGeneratingImages = Object.values(outstandingRequests).some(
		(req) =>
			req?.type === 'do_generate_post_images_for_artifact' &&
			req.artifact_id === artifact.id,
	);
	// A live regeneration request always wins. Checked before the has-images
	// short-circuit so a Retry on a PARTIAL carousel (some slides already
	// present) still shows the generating state instead of silently doing
	// nothing visible.
	if (isGeneratingImages) return true;
	const hasImages = (artifact.content.image_urls?.length ?? 0) > 0;
	if (hasImages) return false;
	const hasGenerationError = !!artifact.content.last_generation_error;
	// Images are only generated once the owning process is activated, so an
	// image-less artifact attached to a not-yet-active process (e.g. a proposed
	// campaign awaiting customer activation) must not spin forever. Prefer a
	// status passed by the caller; otherwise read the on-demand setup snapshot.
	const snapshotStatus = artifact.process_id
		? bridgeApi.user_state?.process_setup_snapshots?.[artifact.process_id]
				?.status
		: undefined;
	const resolvedStatus = processStatus ?? snapshotStatus;
	return (
		isGeneratingImages ||
		(!hasGenerationError &&
			!!artifact.process_id &&
			resolvedStatus === 'active')
	);
}
